import type { Mode } from "../content/modes";
import type { StripeMarks } from "../content/trials";
import { nextTrial } from "./trials";

/** Stripes are earned on the way to a trial's pass mark: at most three per trial. */
export const MAX_STRIPES = 3;

export type StripeMap = Partial<Record<Mode, Record<string, number>>>;

/** How many stripe marks a score reaches. */
export function stripesFor(score: number, marks: StripeMarks): number {
  return marks.filter((m) => score >= m).length;
}

/**
 * Stripes shown on the current rank card in `mode`: the ones earned on the
 * next trial. Zero at the top rank.
 */
export function cardStripes(stripes: StripeMap, mode: Mode, rank: number): number {
  const next = nextTrial(mode, rank);
  if (!next) return 0;
  return Math.min(MAX_STRIPES, stripes[mode]?.[String(next.rankIndex)] ?? 0);
}

/** Record stripes from a score on the trial for `rankIndex`. Stripes are never lost. Pure. */
export function addStripes(
  stripes: StripeMap,
  mode: Mode,
  rankIndex: number,
  earned: number,
): StripeMap {
  const key = String(rankIndex);
  const had = stripes[mode]?.[key] ?? 0;
  if (earned <= had) return stripes;
  return { ...stripes, [mode]: { ...stripes[mode], [key]: Math.min(MAX_STRIPES, earned) } };
}

/** The next stripe mark above a score, or null if every stripe is reached. */
export function nextStripeMark(score: number, marks: StripeMarks): number | null {
  return marks.find((m) => m > score) ?? null;
}
